import { ChangeDetectionStrategy, Component, type OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';

/** Allowed SSO domains — automatic Reader access; ported from the old app-settings dialog. */
@Component({
    selector: 'app-namespace-domains',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, TooltipModule, MessageModule, SectionCardComponent, EmptyStateComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './namespace-section.scss',
    template: `
        <app-section-card title="Sign-in domains" subtitle="Users who sign in via SSO with an email address from these domains automatically receive Reader access to this tenant." icon="pi pi-globe" [count]="domains().length" [flush]="true">
            <div class="app-card-pad">
                <div class="ns-field" style="max-width: 32rem">
                    <label for="new-domain">Add domain</label>
                    <div class="ns-inline">
                        <input id="new-domain" pInputText [(ngModel)]="newDomain" placeholder="company.com" (keyup.enter)="add()" [disabled]="loading()" autocomplete="off" />
                        <p-button label="Add" icon="pi pi-plus" (onClick)="add()" [disabled]="loading() || !newDomain().trim()" />
                    </div>
                    <small>Enter just the domain (e.g. yourcompany.com), not full email addresses.</small>
                </div>
            </div>

            @if (domains().length > 0) {
                <ul class="ns-list" aria-label="Allowed domains">
                    @for (domain of domains(); track domain) {
                        <li>
                            <span class="ns-key"><i class="pi pi-at text-color-secondary" aria-hidden="true"></i>{{ domain }}</span>
                            <p-button icon="pi pi-trash" severity="danger" [text]="true" [rounded]="true" pTooltip="Remove domain" tooltipPosition="left" [ariaLabel]="'Remove ' + domain" (onClick)="remove(domain)" [disabled]="loading()" />
                        </li>
                    }
                </ul>
            } @else if (!loading()) {
                <app-empty-state icon="pi pi-globe" title="No allowed domains" description="Add a domain to let its SSO users in as Readers automatically." [compact]="true" />
            }
        </app-section-card>
    `
})
export class NamespaceDomains implements OnInit {
    private api = inject(CloudApiService);
    private route = inject(ActivatedRoute);
    private messageService = inject(MessageService);

    private namespaceId = signal('');
    domains = signal<string[]>([]);
    loading = signal(false);
    newDomain = signal('');

    ngOnInit(): void {
        this.namespaceId.set(this.route.parent?.snapshot.paramMap.get('namespaceId') ?? '');
        this.refresh();
    }

    refresh() {
        if (!this.namespaceId()) return;
        this.loading.set(true);
        this.api
            .getNamespaceAllowedDomains(this.namespaceId())
            .then((res) => this.domains.set(res.domains ?? []))
            .catch((err) => this.toastError('Failed to load domains', err))
            .finally(() => this.loading.set(false));
    }

    add() {
        const domain = this.newDomain().trim().toLowerCase();
        if (!domain) return;
        this.loading.set(true);
        this.api
            .addNamespaceAllowedDomain(this.namespaceId(), domain)
            .then((res) => {
                this.domains.set(res.domains ?? []);
                this.newDomain.set('');
                this.messageService.add({ severity: 'success', summary: 'Domain Added', detail: domain });
            })
            .catch((err) => this.toastError('Add Failed', err))
            .finally(() => this.loading.set(false));
    }

    remove(domain: string) {
        this.loading.set(true);
        this.api
            .removeNamespaceAllowedDomain(this.namespaceId(), domain)
            .then((res) => {
                this.domains.set(res.domains ?? []);
                this.messageService.add({ severity: 'success', summary: 'Domain Removed', detail: domain });
            })
            .catch((err) => this.toastError('Remove Failed', err))
            .finally(() => this.loading.set(false));
    }

    private toastError(summary: string, err: any) {
        const detail = err?.error?.message ?? (typeof err?.error === 'string' ? err.error : null) ?? err?.message ?? 'An error occurred';
        this.messageService.add({ severity: 'error', summary, detail });
    }
}
